/**
 * Plan/persona efectivos al abrir ticket ?t= (CONTRATO-UDV-ELEVATION.md).
 * En developer (dev-udv / localhost): tickets patient_share pueden elevarse
 * y, si no hay elevación real, aplica plan del lab (simulate / URL / premium).
 *
 * Prod: patient_share sin elev → paciente. patient_share|clinical + elev JWT
 * válido (o sessionStorage de canje en esta pestaña) → plan plataforma.
 */
import {
  ENTITLEMENT_SOURCES,
  TICKET_PURPOSE,
  UDV_PERSONAS,
  UDV_PLANS,
} from '../plan/constants'
import { parseThemeQueryParam } from '../plan/chromeTheme'
import {
  pickProfileForTicketLoad,
  resolveOriginTicketElevation,
} from '../plan/clinicalElevation'
import {
  parsePlanQueryParam,
  parseShellFromSearchParams,
  shellShortFromLayout,
} from '../plan/parsePlanQuery'
import {
  getEffectivePlan,
  getEffectiveShellHint,
  isDeveloperMode,
} from '../plan/resolveAccess'
import { fetchUdvElevation } from './viewerTicket'
import {
  matchPlatformElevationSession,
  platformElevationSessionToAccess,
  savePlatformElevationSession,
} from '../plan/platformElevationSession'

/**
 * @typedef {object} TicketAccessContext
 * @property {boolean} elevated
 * @property {string} plan
 * @property {'portal'|'clinical'} persona
 * @property {string} loadProfile
 * @property {string|null} shell
 * @property {string|null} [theme]
 * @property {object|null} entitlements
 * @property {string} source
 * @property {'clinical'|'portal'|'legacy'|null} [mode]
 */

/**
 * @param {object} ticket normalizeTicketResponse
 * @returns {'patient_share'|'clinical'}
 */
export function ticketPurposeOf(ticket) {
  const p = ticket && ticket.purpose
  if (p === TICKET_PURPOSE.CLINICAL) return TICKET_PURPOSE.CLINICAL
  return TICKET_PURPOSE.PATIENT_SHARE
}

/**
 * @param {{
 *   ticket: object,
 *   ticketId: string,
 *   ticketApiBase: string,
 *   elevationToken?: string|null,
 *   originEdgeId?: string|null,
 *   controllerLinks?: object[]|null,
 *   cachedEnt?: object|null,
 *   searchParams?: URLSearchParams|null,
 *   developerMode?: boolean,
 *   fetchImpl?: typeof fetch,
 * }} opts
 * @returns {Promise<TicketAccessContext>}
 */
export async function resolveTicketAccessContext(opts) {
  const ticket = opts.ticket || {}
  const ticketId = String(opts.ticketId || '').trim()
  const ticketProfile = ticket.profile || UDV_PLANS.PATIENT
  const purpose = ticketPurposeOf(ticket)
  const developerMode =
    opts.developerMode !== undefined ? opts.developerMode : isDeveloperMode()
  const searchParams = opts.searchParams || null

  const elevToken =
    opts.elevationToken != null && String(opts.elevationToken).trim() !== ''
      ? String(opts.elevationToken).trim()
      : null

  // Canje elev (staff clinical-open) — también sobre tickets patient_share.
  if (elevToken && ticketId) {
    const redeemed = await fetchUdvElevation(ticketId, {
      baseUrl: opts.ticketApiBase,
      elevationToken: elevToken,
      fetchImpl: opts.fetchImpl,
    })
    if (redeemed.ok && redeemed.authorized && redeemed.plan) {
      const ctx = platformElevationContext(redeemed, ticketProfile)
      savePlatformElevationSession({
        ticketId,
        plan: ctx.plan,
        shell: ctx.shell,
        theme: ctx.theme,
        edgeId:
          (ctx.entitlements && ctx.entitlements.edgeId) ||
          opts.originEdgeId ||
          null,
        leaseToken: redeemed.leaseToken || null,
        leaseExpiresAt: redeemed.leaseExpiresAt || null,
        extendIntervalSeconds: redeemed.extendIntervalSeconds || null,
        ticketApiBase: opts.ticketApiBase || null,
      })
      return ctx
    }
    // One-shot ya canjeado (Strict Mode / carrera): usar session si existe.
    if (redeemed.reason === 'token_already_used') {
      const platUsed = matchPlatformElevationSession(ticketId)
      if (platUsed) {
        return platformElevationSessionToAccess(platUsed, ticketProfile)
      }
    }
  }

  // Misma pestaña tras strip de elev (share-safe en otra pestaña no tiene sessionStorage).
  const platSession = matchPlatformElevationSession(ticketId)
  if (platSession) {
    return platformElevationSessionToAccess(platSession, ticketProfile)
  }

  // Sin elev ni session: patient_share → portal (fail-safe). Clinical sigue a links[].
  if (purpose === TICKET_PURPOSE.PATIENT_SHARE && !developerMode) {
    return portalContext(ticketProfile, ticket)
  }

  const originEdgeId =
    opts.originEdgeId != null && String(opts.originEdgeId).trim() !== ''
      ? String(opts.originEdgeId).trim()
      : null
  const elevation = resolveOriginTicketElevation(
    originEdgeId,
    opts.controllerLinks,
    opts.cachedEnt,
    null
  )

  if (elevation.elevated) {
    const plan =
      elevation.plan ||
      (elevation.entitlements && elevation.entitlements.plan) ||
      UDV_PLANS.LITE
    const shell =
      elevation.entitlements && elevation.entitlements.shell
        ? elevation.entitlements.shell
        : ticket.shell || null
    const theme =
      (elevation.entitlements &&
        parseThemeQueryParam(elevation.entitlements.theme)) ||
      parseThemeQueryParam(ticket.theme) ||
      null
    return {
      elevated: true,
      plan,
      persona: UDV_PERSONAS.CLINICAL,
      loadProfile: pickProfileForTicketLoad(ticketProfile, true, plan),
      shell,
      theme,
      entitlements: elevation.entitlements
        ? { ...elevation.entitlements, theme: theme || elevation.entitlements.theme || null }
        : null,
      source:
        elevation.mode === 'clinical'
          ? ENTITLEMENT_SOURCES.ORIGIN_LINK
          : ENTITLEMENT_SOURCES.CONTROLLER,
      mode: elevation.mode,
    }
  }

  if (developerMode) {
    return developerLabContext(ticketProfile, ticket, searchParams, opts.cachedEnt)
  }

  return portalContext(ticketProfile, ticket)
}

/**
 * @param {object} redeemed fetchUdvElevation authorized
 * @param {string} ticketProfile
 */
function platformElevationContext(redeemed, ticketProfile) {
  const plan = parsePlanQueryParam(redeemed.plan) || UDV_PLANS.LITE
  const shell =
    redeemed.shell != null && String(redeemed.shell).trim() !== ''
      ? String(redeemed.shell).trim()
      : null
  const theme = parseThemeQueryParam(redeemed.theme)
  const edgeId =
    redeemed.edgeId != null && String(redeemed.edgeId).trim() !== ''
      ? String(redeemed.edgeId).trim()
      : null
  return {
    elevated: true,
    plan,
    persona: UDV_PERSONAS.CLINICAL,
    loadProfile: pickProfileForTicketLoad(ticketProfile, true, plan),
    shell,
    theme,
    entitlements: {
      plan,
      shell,
      theme,
      pacsId: null,
      udvToken: null,
      edgeId,
      clinicName: null,
      source: ENTITLEMENT_SOURCES.PLATFORM_ELEVATION,
    },
    source: ENTITLEMENT_SOURCES.PLATFORM_ELEVATION,
    mode: 'clinical',
  }
}

/**
 * Lab dev-udv: plan/shell del panel DEVELOPER o query (?plan= / ?udvToken=).
 * @param {string} ticketProfile
 * @param {object} ticket
 * @param {URLSearchParams|null} searchParams
 * @param {object|null|undefined} cachedEnt
 */
function developerLabContext(ticketProfile, ticket, searchParams, cachedEnt) {
  const params =
    searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  const plan = getEffectivePlan({
    searchParams: params,
    entitlements: cachedEnt !== undefined ? cachedEnt : undefined,
  })
  if (plan === UDV_PLANS.PATIENT) {
    return portalContext(ticketProfile, ticket)
  }
  const shellLayout = getEffectiveShellHint({
    searchParams: params,
    entitlements: cachedEnt !== undefined ? cachedEnt : undefined,
  })
  const shellFromUrl = parseShellFromSearchParams(params)
  // Ticket JSON gana sobre simulate/URL lab (evita Studio cuando ticket pide classic).
  const shell =
    ticket.shell ||
    (shellFromUrl && shellShortFromLayout(shellFromUrl)) ||
    (shellLayout && shellShortFromLayout(shellLayout)) ||
    null
  return {
    elevated: true,
    plan,
    persona: UDV_PERSONAS.CLINICAL,
    loadProfile: pickProfileForTicketLoad(ticketProfile, true, plan),
    shell,
    theme: parseThemeQueryParam(ticket.theme),
    entitlements: null,
    source: 'developer',
    mode: 'legacy',
  }
}

/**
 * Portal paciente: aplica shell/theme del ticket (MANUAL-VIEWER-TICKETS).
 * @param {string} ticketProfile
 * @param {object|null} [ticket]
 * @returns {TicketAccessContext}
 */
function portalContext(ticketProfile, ticket = null) {
  const shellRaw =
    ticket && ticket.shell != null && String(ticket.shell).trim() !== ''
      ? String(ticket.shell).trim()
      : null
  return {
    elevated: false,
    plan: UDV_PLANS.PATIENT,
    persona: UDV_PERSONAS.PORTAL,
    loadProfile: UDV_PLANS.PATIENT,
    shell: shellRaw,
    theme: ticket ? parseThemeQueryParam(ticket.theme) : null,
    entitlements: null,
    source: 'ticket_portal',
    mode: 'portal',
  }
}
